import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  ArrowLeft,
  CircleNotch,
  Phone,
  Envelope,
  MapPin,
  Building,
  Globe,
  Pencil,
  Trash,
  Plus,
  FileText
} from '@phosphor-icons/react';


const STATUS_OPTIONS = ['Lead', 'Prospect', 'Client', 'Inactive'];
const LANGUAGE_OPTIONS = [
  { value: 'ES', label: 'Español' },
  { value: 'EN', label: 'English' }
];

function ClientStatusBadge({ status }) {
  const styles = {
    'Lead': 'badge-lead',
    'Prospect': 'badge-prospect',
    'Client': 'badge-client',
    'Inactive': 'badge-inactive',
  };

  return (
    <span className={`px-3 py-1.5 rounded-full text-sm font-semibold ${styles[status] || 'badge-lead'}`}>
      {status}
    </span>
  );
}

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [client, setClient] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    service_interest: '',
    notes: '',
    status: 'Lead',
    preferred_language: 'ES'
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(isNew);
  const [quotes, setQuotes] = useState([]);

  useEffect(() => {
    if (!isNew) {
      fetchClient();
      fetchClientQuotes();
    }
  }, [id, isNew]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/${id}`, {
        withCredentials: true
      });
      setClient(response.data);
    } catch (error) {
      console.error('Error fetching client:', error);
      navigate('/clients');
    } finally {
      setLoading(false);
    }
  };

  const fetchClientQuotes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/quotes?client_id=${id}`, {
        withCredentials: true
      });
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API_URL}/api/clients`, client, { withCredentials: true });
      } else {
        await axios.put(`${API_URL}/api/clients/${id}`, client, { withCredentials: true });
      }
      navigate('/clients');
    } catch (error) {
      console.error('Error saving client:', error);
      alert('Error al guardar el cliente');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar este cliente?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/clients/${id}`, { withCredentials: true });
      navigate('/clients');
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Error al eliminar el cliente');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={40} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/clients')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Nuevo Cliente' : editing ? 'Editar Cliente' : client.name}
          </h1>
          {!isNew && !editing && (
            <div className="flex items-center gap-2 mt-1">
              <ClientStatusBadge status={client.status} />
            </div>
          )}
        </div>
        {!isNew && !editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              data-testid="edit-button"
            >
              <Pencil size={18} />
              Editar
            </button>
            <button
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
              data-testid="delete-button"
            >
              <Trash size={18} />
              Eliminar
            </button>
          </div>
        )}
      </div>

      {editing ? (
        /* Edit Form */
        <form onSubmit={handleSave} className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="form-label">Nombre completo *</label>
              <input
                type="text"
                value={client.name}
                onChange={(e) => setClient({...client, name: e.target.value})}
                className="form-input"
                required
                data-testid="name-input"
              />
            </div>
            <div>
              <label className="form-label">Empresa</label>
              <input
                type="text"
                value={client.company || ''}
                onChange={(e) => setClient({...client, company: e.target.value})}
                className="form-input"
                data-testid="company-input"
              />
            </div>
            <div>
              <label className="form-label">Teléfono *</label>
              <input
                type="tel"
                value={client.phone}
                onChange={(e) => setClient({...client, phone: e.target.value})}
                className="form-input"
                required
                data-testid="phone-input"
              />
            </div>
            <div>
              <label className="form-label">Email *</label>
              <input
                type="email"
                value={client.email}
                onChange={(e) => setClient({...client, email: e.target.value})}
                className="form-input"
                required
                data-testid="email-input"
              />
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Dirección</label>
              <input
                type="text"
                value={client.address || ''}
                onChange={(e) => setClient({...client, address: e.target.value})}
                className="form-input"
                data-testid="address-input"
              />
            </div>
            <div>
              <label className="form-label">Ciudad</label>
              <input
                type="text"
                value={client.city || ''}
                onChange={(e) => setClient({...client, city: e.target.value})}
                className="form-input"
                data-testid="city-input"
              />
            </div>
            <div>
              <label className="form-label">Estado</label>
              <input
                type="text"
                value={client.state || ''}
                onChange={(e) => setClient({...client, state: e.target.value})}
                className="form-input"
                data-testid="state-input"
              />
            </div>
            <div>
              <label className="form-label">Código Postal</label>
              <input
                type="text"
                value={client.zip_code || ''}
                onChange={(e) => setClient({...client, zip_code: e.target.value})}
                className="form-input"
                data-testid="zip-input"
              />
            </div>
            <div>
              <label className="form-label">Servicio de interés</label>
              <input
                type="text"
                value={client.service_interest || ''}
                onChange={(e) => setClient({...client, service_interest: e.target.value})}
                className="form-input"
                placeholder="ej: CCTV, Control de Acceso, Alarmas..."
                data-testid="service-input"
              />
            </div>
            <div>
              <label className="form-label">Estado</label>
              <select
                value={client.status}
                onChange={(e) => setClient({...client, status: e.target.value})}
                className="form-input"
                data-testid="status-select"
              >
                {STATUS_OPTIONS.map(status => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Idioma preferido</label>
              <select
                value={client.preferred_language}
                onChange={(e) => setClient({...client, preferred_language: e.target.value})}
                className="form-input"
                data-testid="language-select"
              >
                {LANGUAGE_OPTIONS.map(lang => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="form-label">Notas</label>
              <textarea
                value={client.notes || ''}
                onChange={(e) => setClient({...client, notes: e.target.value})}
                className="form-input"
                rows={4}
                data-testid="notes-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6 pt-6 border-t border-slate-200">
            <button
              type="button"
              onClick={() => isNew ? navigate('/clients') : setEditing(false)}
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
              data-testid="cancel-button"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              data-testid="save-button"
            >
              {saving && <CircleNotch size={18} className="animate-spin" />}
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      ) : (
        /* View Mode */
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Info */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="text-lg font-semibold mb-4">Información de Contacto</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Phone size={20} className="text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Teléfono</p>
                    <p className="font-medium">{client.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Envelope size={20} className="text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Email</p>
                    <p className="font-medium">{client.email}</p>
                  </div>
                </div>
                {client.company && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Building size={20} className="text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Empresa</p>
                      <p className="font-medium">{client.company}</p>
                    </div>
                  </div>
                )}
                {client.address && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <MapPin size={20} className="text-slate-400" />
                    <div>
                      <p className="text-xs text-slate-500">Dirección</p>
                      <p className="font-medium">
                        {client.address}<br/>
                        {client.city && `${client.city}, `}{client.state} {client.zip_code}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Globe size={20} className="text-slate-400" />
                  <div>
                    <p className="text-xs text-slate-500">Idioma</p>
                    <p className="font-medium">
                      {client.preferred_language === 'ES' ? 'Español' : 'English'}
                    </p>
                  </div>
                </div>
              </div>

              {client.service_interest && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-1">Servicio de interés</p>
                  <p className="font-medium">{client.service_interest}</p>
                </div>
              )}

              {client.notes && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-1">Notas</p>
                  <p className="text-slate-700 whitespace-pre-wrap">{client.notes}</p>
                </div>
              )}
            </div>

            {/* Quotes for this client */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Cotizaciones</h2>
                <Link
                  to={`/quotes/new?client_id=${id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                  data-testid="new-quote-button"
                >
                  <Plus size={16} />
                  Nueva
                </Link>
              </div>
              {quotes.length > 0 ? (
                <div className="divide-y divide-slate-100">
                  {quotes.map((quote) => (
                    <Link
                      key={quote.id}
                      to={`/quotes/${quote.id}`}
                      className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText size={20} className="text-slate-400" />
                        <div>
                          <p className="font-medium">{quote.quote_number}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(quote.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${quote.total?.toLocaleString()}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full badge-${quote.status?.toLowerCase().replace(' ', '-')}`}>
                          {quote.status}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-slate-500">
                  No hay cotizaciones para este cliente
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">
                Información
              </h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-slate-500">Creado</p>
                  <p className="font-medium">
                    {new Date(client.created_at).toLocaleDateString('es-ES', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total cotizaciones</p>
                  <p className="font-medium">{quotes.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
